import {
  Alert,
  Autocomplete,
  Button,
  Grid,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from "notistack";
import { exportAll, findCenterCombo, findCustomReport } from "../../../../../services/report";
import { ComboBoxType } from "../../../../../types/general";
import AddIcon from '@mui/icons-material/Add';
import { Download } from "@mui/icons-material";

interface BasicFormReportProps {
  setParams: (params: any) => void;
  setIsLoading: (value: boolean) => void;
  isLoading?: boolean;
  errors?: string[];
  ID_RELATORIO: number;
  setCustomReport: (value: boolean) => void;
  reportSelected: ComboBoxType;
  setReportSelected: (comboSelected: ComboBoxType) => void;
  customReportCombo: ComboBoxType[];
  setCustomReportCombo: (combo: ComboBoxType[]) => void;
}

export default function BasicFormReport({
  setIsLoading,
  setParams,
  isLoading = false,
  errors = [],
  ID_RELATORIO,
  setCustomReport,
  reportSelected,
  setReportSelected,
  customReportCombo,
  setCustomReportCombo,
}: BasicFormReportProps) {

  const [centerSelected, setCenterSelected] = useState();
  const [centerCombo, setCenterCombo] = useState<ComboBoxType[]>([]);
  const [isLoadingCenter, setIsLoadingCenter] = useState(true);
  const [isLoadingReportCustom, setIsLoadingReportCustom] = useState(true);

  const { enqueueSnackbar } = useSnackbar();

  const handleFormSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if(!reportSelected?.id) {
      enqueueSnackbar("You must to select Report", {
        variant: "warning",
      });
      return;
    }
    setParams((prevState: any) => ({
      ...prevState,
      count: prevState.count ? prevState.count + 1 : prevState.count,
      page: 1,
      ID_RELATORIO_CUSTOM: reportSelected?.id,
      ID_CENTRO: centerSelected,
    }));
  }

  const exportFile = async () => {
    if(!reportSelected?.id) {
      enqueueSnackbar("You must to select Report", {
        variant: "warning",
      });
      return;
    }
    setIsLoading(true);
    try {
      const response = await exportAll({
        ID_RELATORIO_CUSTOM: reportSelected?.id,
        ID_CENTRO: centerSelected,
      });
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${reportSelected.description}.xlsx`;

      if (contentDisposition && contentDisposition.includes('filename=')) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch.length > 1) {
          filename = filenameMatch[1];
        }
      }

      const href = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (err) {}
    finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    findCenterCombo().then((centerResponse) => {
      setCenterCombo(centerResponse);
      setIsLoadingCenter(false);
    });
  }, [])

  useEffect(() => {
    const fetchCustomReportData = async () => {
      try {
        const customReportResponse = await findCustomReport(ID_RELATORIO);
        setCustomReportCombo(customReportResponse || []);  // Default to empty array

        const founded = customReportResponse?.findLast((item) => item.description === 'Default');
        if (founded) {
          setReportSelected(founded);
        }

        setIsLoadingReportCustom(false);
      } catch (error) {
        console.error("Error fetching custom report:", error);
        setIsLoadingReportCustom(false);
      }
    };

    fetchCustomReportData();
  }, [ID_RELATORIO]);

  return (
    <form
      style={{ width: "100%" }}
      data-testid="report-form"
    >
      {errors.map((error) => (
        <Alert key={error} severity="error" style={{ marginBottom: 20 }}>
          {error}
        </Alert>
      ))}
      <Grid container spacing={2} marginBottom={2} marginTop={4}>
        <Grid item md={10} xs={10} display='flex' justifyContent='end'>
          <Button
            startIcon={<AddIcon />}
            variant='contained'
            size='small'
            onClick={() => setCustomReport(true)}
          >
            Custom
          </Button>
        </Grid>
        <Grid item md={5} xs={5}>
          <Autocomplete
            options={centerCombo || []}
            getOptionLabel={(option) => option.description}
            loading={isLoadingCenter}
            renderInput={(params: any) => (
              <TextField
                {...params}
                label="Company - Plant"
                size="small"
                variant="outlined"
                data-testid="company-plant-input"
              />
            )}
            onChange={(_: any, data: any) => setCenterSelected(data?.id)}
          />
        </Grid>
        <Grid item md={5} xs={5}>
          <Autocomplete
            options={customReportCombo || []}
            getOptionLabel={(option) => option.description}
            value={ reportSelected }
            loading={isLoadingReportCustom}
            renderInput={(params: any) => (
              <TextField
                {...params}
                label="Custom Report"
                size="small"
                variant="outlined"
                data-testid="custom-report-input"
                // defaultValue={() => {}}
              />
            )}
            onChange={(_: any, data: any) => setReportSelected(data)}
          />
        </Grid>
        <Grid item md={2} xs={2}>
          <Button
            startIcon={<SearchIcon />}
            variant="outlined"
            disabled={isLoading}
            data-testid="submit-button"
            onClick={handleFormSearch}
          >
            Search
          </Button>
        </Grid>
      </Grid>
      <Grid container marginTop={4} justifyContent="flex-end">
        <Button
          variant="text"
          onClick={exportFile}
          startIcon={<Download />}
          disabled={isLoading}
        >
          Export XLSX
        </Button>
      </Grid>
    </form>
  );
}
